<script setup lang="ts">
import { Form, Head, router, usePage } from "@inertiajs/vue3";
import {
  AlertCircleIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MailPlusIcon,
  MessageSquarePlusIcon,
  SearchIcon,
  UserIcon,
  UsersIcon,
} from "lucide-vue-next";
import { computed, onBeforeUnmount, ref, watch } from "vue";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  newOrganizationDiscussionThreadPath,
  organizationDirectoryPath,
  organizationTenantInvitationsPath,
} from "@/routes";
import type { MentionableGroup, MentionableUser, SharedData } from "@/types";
import { initialsFromName } from "@/utils/initials";

defineOptions({ inheritAttrs: false });

interface Props {
  query: string;
  users: MentionableUser[];
  groups: MentionableGroup[];
  selected_user_ids: number[];
  selected_group_ids: number[];
  can_invite: boolean;
}

type FilterTab = "todos" | "personas" | "grupos";

const props = defineProps<Props>();
const page = usePage<SharedData>();

const organizationId = computed(() => page.props.auth.organization?.id);

const search = ref(props.query || "");
const filter = ref<FilterTab>("todos");
const selectedUsers = ref<Set<number>>(new Set(props.selected_user_ids || []));
const selectedGroups = ref<Set<number>>(new Set(props.selected_group_ids || []));
const expandedGroups = ref<Set<number>>(new Set());

watch(
  () => props.selected_user_ids,
  (incoming) => {
    selectedUsers.value = new Set(incoming || []);
  },
);

watch(
  () => props.selected_group_ids,
  (incoming) => {
    selectedGroups.value = new Set(incoming || []);
  },
);

watch(
  () => props.query,
  (incoming) => {
    if (incoming !== search.value) search.value = incoming || "";
  },
);

let searchTimeoutId: number | undefined;

watch(search, (value) => {
  if (!organizationId.value) return;

  if (searchTimeoutId) window.clearTimeout(searchTimeoutId);
  searchTimeoutId = window.setTimeout(() => {
    router.get(
      organizationDirectoryPath(organizationId.value!),
      {
        query: value,
        user_ids: Array.from(selectedUsers.value),
        group_ids: Array.from(selectedGroups.value),
      },
      {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        only: ["query", "users", "groups", "selected_user_ids", "selected_group_ids"],
      },
    );
  }, 250);
});

onBeforeUnmount(() => {
  if (searchTimeoutId) window.clearTimeout(searchTimeoutId);
});

const normalizedSearch = computed(() => search.value.trim().toLowerCase());

const filteredUsers = computed(() => {
  if (!normalizedSearch.value) return props.users;

  return props.users.filter((user) => {
    const fields = [
      user.name,
      user.role_title || "",
      user.public_bio || "",
      user.short_bio || "",
      user.email,
      ...(user.discussion_groups || []).map((group) => group.name),
    ]
      .join(" ")
      .toLowerCase();

    return fields.includes(normalizedSearch.value);
  });
});

const filteredGroups = computed(() => {
  if (!normalizedSearch.value) return props.groups;

  return props.groups.filter((group) => {
    const fields = [
      group.name,
      group.description || "",
      ...(group.members || []).flatMap((member) => [
        member.name,
        member.email,
        member.role_title || "",
      ]),
    ]
      .join(" ")
      .toLowerCase();

    return fields.includes(normalizedSearch.value);
  });
});

const showUsers = computed(() => filter.value === "todos" || filter.value === "personas");
const showGroups = computed(() => filter.value === "todos" || filter.value === "grupos");

const totalSelected = computed(() => selectedUsers.value.size + selectedGroups.value.size);

const toggleUser = (id: number) => {
  const next = new Set(selectedUsers.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedUsers.value = next;
};

const toggleGroup = (id: number) => {
  const next = new Set(selectedGroups.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedGroups.value = next;
};

const toggleExpandedGroup = (id: number) => {
  const next = new Set(expandedGroups.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedGroups.value = next;
};

const clearSelection = () => {
  selectedUsers.value = new Set();
  selectedGroups.value = new Set();
};

const createThreadWithSelection = () => {
  if (!organizationId.value || totalSelected.value === 0) return;

  const params = new URLSearchParams();
  Array.from(selectedUsers.value).forEach((id) => params.append("user_ids[]", String(id)));
  Array.from(selectedGroups.value).forEach((id) => params.append("group_ids[]", String(id)));

  const basePath = newOrganizationDiscussionThreadPath(organizationId.value);
  router.visit(params.size > 0 ? `${basePath}?${params.toString()}` : basePath);
};

const isUserIncomplete = (user: MentionableUser) => !user.profile_complete;
const initialsFor = (name: string) => initialsFromName(name);

const inviteDialogOpen = ref(false);
</script>

<template>
  <Head title="Directorio" />

  <div v-if="!organizationId" class="page-container page-section text-sm text-muted-foreground">
    Selecciona una organización para abrir el directorio.
  </div>

  <div v-else class="relative flex h-full min-h-0 w-full flex-col gap-3 bg-background p-3">
    <Card class="gap-3 px-5 py-4 shadow-none">
      <CardHeader class="gap-1 p-0">
        <div class="flex items-start justify-between gap-3">
          <div>
            <CardTitle class="text-base">Directorio</CardTitle>
            <p class="mt-1 text-sm text-muted-foreground">
              Encontrá personas y grupos por nombre, cargo o área, y creá un hilo con participantes correctos.
            </p>
          </div>
          <Button
            v-if="props.can_invite"
            size="sm"
            variant="outline"
            class="shrink-0"
            @click="inviteDialogOpen = true"
          >
            <MailPlusIcon class="mr-1.5 size-3.5" />
            Invitar
          </Button>
        </div>
      </CardHeader>

      <CardContent class="space-y-3 p-0">
        <div class="relative">
          <SearchIcon class="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/70" />
          <Input
            v-model="search"
            class="pl-9"
            type="search"
            placeholder="Buscar personas, grupos, cargo, bio o email..."
          />
        </div>

        <div class="flex flex-wrap items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            :class="filter === 'todos' ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90' : ''"
            @click="filter = 'todos'"
          >
            Todos
            <span class="ml-1 text-xs opacity-70">{{ filteredUsers.length + filteredGroups.length }}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            :class="filter === 'personas' ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90' : ''"
            @click="filter = 'personas'"
          >
            Personas
            <span class="ml-1 text-xs opacity-70">{{ filteredUsers.length }}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            :class="filter === 'grupos' ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90' : ''"
            @click="filter = 'grupos'"
          >
            Grupos
            <span class="ml-1 text-xs opacity-70">{{ filteredGroups.length }}</span>
          </Button>
        </div>
      </CardContent>
    </Card>

    <ScrollArea class="min-h-0 flex-1 rounded-xl border border-border bg-card/80">
      <div class="space-y-6 p-4">
        <section v-if="showUsers && filteredUsers.length > 0" class="space-y-3">
          <div class="flex items-center gap-2 px-1">
            <UserIcon class="size-3.5 text-muted-foreground/60" />
            <h2 class="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
              Personas
            </h2>
          </div>

          <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            <Card
              v-for="user in filteredUsers"
              :key="user.id"
              class="gap-0 rounded-lg border px-4 py-3 shadow-none transition-colors"
              :class="selectedUsers.has(user.id) ? 'border-primary bg-primary/[0.02]' : 'border-border/80'"
            >
              <CardContent class="space-y-3 p-0">
                <div class="flex items-start gap-3">
                  <div class="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                    {{ initialsFor(user.name) }}
                  </div>

                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-1.5">
                      <p class="truncate text-sm font-semibold text-foreground">{{ user.name }}</p>
                      <AlertCircleIcon
                        v-if="isUserIncomplete(user)"
                        class="size-3.5 shrink-0 text-amber-500"
                      />
                    </div>

                    <div v-if="user.role_title" class="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      <BriefcaseIcon class="size-3" />
                      <span class="truncate">{{ user.role_title }}</span>
                    </div>
                    <p v-else class="mt-0.5 text-xs text-amber-600/80">Sin cargo definido</p>
                  </div>

                  <Checkbox
                    :checked="selectedUsers.has(user.id)"
                    class="mt-0.5 self-start"
                    @update:checked="toggleUser(user.id)"
                  />
                </div>

                <p
                  v-if="user.short_bio || user.public_bio"
                  class="line-clamp-2 text-xs leading-relaxed text-muted-foreground"
                >
                  {{ user.short_bio || user.public_bio }}
                </p>
                <p v-else class="text-xs italic text-muted-foreground/50">Perfil sin mini bio.</p>

                <div class="flex flex-wrap gap-1">
                  <Badge
                    v-for="group in user.discussion_groups || []"
                    :key="`user-${user.id}-group-${group.id}`"
                    variant="secondary"
                    class="rounded-full text-[10px]"
                  >
                    @{{ group.name }}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section v-if="showGroups && filteredGroups.length > 0" class="space-y-3">
          <div class="flex items-center gap-2 px-1">
            <UsersIcon class="size-3.5 text-muted-foreground/60" />
            <h2 class="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
              Grupos
            </h2>
          </div>

          <div class="space-y-2">
            <Card
              v-for="group in filteredGroups"
              :key="group.id"
              class="gap-0 rounded-lg border px-4 py-3 shadow-none"
              :class="selectedGroups.has(group.id) ? 'border-primary' : 'border-border/80'"
            >
              <CardContent class="space-y-3 p-0">
                <div class="flex items-start gap-3">
                  <Checkbox
                    :checked="selectedGroups.has(group.id)"
                    class="mt-0.5 self-start"
                    @update:checked="toggleGroup(group.id)"
                  />

                  <div class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <UsersIcon class="size-4" />
                  </div>

                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2">
                      <p class="truncate text-sm font-semibold">@{{ group.name }}</p>
                      <span class="text-xs text-muted-foreground">{{ group.member_count }} miembros</span>
                    </div>

                    <p v-if="group.description" class="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {{ group.description }}
                    </p>

                    <div class="mt-2 flex items-center gap-2">
                      <div class="flex -space-x-1.5">
                        <div
                          v-for="member in (group.member_preview || group.members || []).slice(0, 4)"
                          :key="`preview-${group.id}-${member.id}`"
                          class="flex size-6 items-center justify-center rounded-full border-2 border-card bg-muted text-[8px] font-semibold text-muted-foreground"
                        >
                          {{ initialsFor(member.name) }}
                        </div>
                        <div
                          v-if="group.member_count > 4"
                          class="flex size-6 items-center justify-center rounded-full border-2 border-card bg-background text-[8px] font-semibold text-muted-foreground"
                        >
                          +{{ group.member_count - 4 }}
                        </div>
                      </div>

                      <button
                        type="button"
                        class="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                        @click="toggleExpandedGroup(group.id)"
                      >
                        {{ expandedGroups.has(group.id) ? "Ocultar" : "Ver miembros" }}
                        <ChevronUpIcon v-if="expandedGroups.has(group.id)" class="size-3" />
                        <ChevronDownIcon v-else class="size-3" />
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  v-if="expandedGroups.has(group.id)"
                  class="space-y-1 border-t border-border pt-3"
                >
                  <div
                    v-for="member in group.members || []"
                    :key="`member-${group.id}-${member.id}`"
                    class="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-muted/40"
                  >
                    <div class="flex size-7 items-center justify-center rounded-full bg-muted text-[9px] font-semibold text-muted-foreground">
                      {{ initialsFor(member.name) }}
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-sm font-medium text-foreground">{{ member.name }}</p>
                      <p v-if="member.role_title" class="truncate text-xs text-muted-foreground">{{ member.role_title }}</p>
                    </div>
                    <span class="truncate text-[11px] text-muted-foreground/70">{{ member.email }}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <div
          v-if="filteredUsers.length === 0 && filteredGroups.length === 0"
          class="flex flex-col items-center justify-center py-16"
        >
          <SearchIcon class="mb-2 size-5 text-muted-foreground/40" />
          <p class="text-sm text-muted-foreground">Sin resultados para "{{ search }}"</p>
          <p class="text-xs text-muted-foreground/70">Probá con otro nombre, cargo o área.</p>
        </div>
      </div>
    </ScrollArea>

    <div v-if="totalSelected > 0" class="pointer-events-none absolute bottom-4 left-1/2 z-20 w-full max-w-2xl -translate-x-1/2 px-3">
      <Card class="pointer-events-auto gap-0 rounded-2xl border-primary/20 bg-background/95 px-4 py-3 shadow-lg backdrop-blur">
        <CardContent class="flex items-center gap-3 p-0">
          <p class="text-sm font-medium text-foreground">
            {{ totalSelected }} seleccionado{{ totalSelected > 1 ? "s" : "" }}
          </p>

          <span class="h-5 w-px bg-border" />

          <Button variant="ghost" size="sm" class="h-8 px-2 text-muted-foreground" @click="clearSelection">
            Limpiar
          </Button>

          <Button class="ml-auto h-8" size="sm" @click="createThreadWithSelection">
            <MessageSquarePlusIcon class="mr-1.5 size-4" />
            Crear hilo
          </Button>
        </CardContent>
      </Card>
    </div>
  </div>

  <!-- ── Invite dialog ── -->
  <Dialog v-if="props.can_invite" v-model:open="inviteDialogOpen">
    <DialogContent class="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle>Invitar persona</DialogTitle>
        <DialogDescription>
          Ingresá el email de la persona que querés invitar a la organización.
        </DialogDescription>
      </DialogHeader>
      <Form
        :action="organizationTenantInvitationsPath(organizationId!)"
        method="post"
        class="space-y-4"
        #default="{ processing, errors }"
        @success="inviteDialogOpen = false"
      >
        <div class="space-y-1.5">
          <Label for="invite-email">Email</Label>
          <Input
            id="invite-email"
            name="email"
            type="email"
            placeholder="nombre@empresa.com"
            required
          />
          <p v-if="errors.email" class="text-xs text-destructive">
            {{ errors.email }}
          </p>
        </div>

        <Button type="submit" class="w-full" :disabled="processing">
          <MailPlusIcon class="mr-1.5 size-4" />
          {{ processing ? "Enviando…" : "Enviar invitación" }}
        </Button>
      </Form>
    </DialogContent>
  </Dialog>
</template>
